import React from 'react';
import { Marker, Popup, CircleMarker, Tooltip } from 'react-leaflet';

import type { LocationModel } from '@/entities/location';

import { LocationWithData, DataLayer, LocationPopup } from '@/features/map-locations';
import { getLocationIcon } from '../leaflet-icons';
import { calculateCircleRadius, getCircleColor } from '../lib/map-calculations';

interface LocationLayerProps {
  location: LocationModel | LocationWithData;
  selectedLocation?: LocationModel | null;
  dataLayer: DataLayer;
  currentWeek: number;
  onLocationSelect?: (location: LocationModel) => void;
}

export const LocationLayer = React.memo(
  ({ location, selectedLocation, dataLayer, currentWeek, onLocationSelect }: LocationLayerProps) => {
    const dataValue = 'dataValue' in location ? (location.dataValue ?? 0) : 0;
    const hasData = 'dataValue' in location && dataValue > 0;
    const isSelected = selectedLocation?.id === location.id;

    return (
      <>
        <Marker
          position={[location.lat, location.lng]}
          icon={getLocationIcon(location.type, isSelected)}
          eventHandlers={{
            click: () => onLocationSelect?.(location)
          }}>
          <Popup maxWidth={400} minWidth={300}>
            <LocationPopup location={location} dataLayer={dataLayer} currentWeek={currentWeek} />
          </Popup>
          {hasData && (
            <Tooltip permanent direction="top" offset={[0, -20]} className="custom-tooltip">
              <span className="text-xs font-semibold">{Math.round(dataValue)}</span>
            </Tooltip>
          )}
        </Marker>

        {hasData && (
          <CircleMarker
            key={`circle-${location.id}-${dataValue}`}
            center={[location.lat, location.lng]}
            radius={calculateCircleRadius(dataValue)}
            fillColor={getCircleColor(dataLayer)}
            fillOpacity={0.4}
            stroke={false}
            eventHandlers={{
              click: () => onLocationSelect?.(location)
            }}
          />
        )}
      </>
    );
  }
);
